import type { FC } from "react";
import { useState } from "react";

import { SelectedAddressViewCard, useFeatureFlags } from "@jti/commerce";
import type { AddressHomeDeliveryFieldValue } from "@jti/commerce-types";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { Button } from "@jti/core-components/components/Button";
import type { AddressStored } from "@jti/fe-common";
import { AddressInfoBlock, useTranslations } from "@jti/ui";

import type { AddressHomeDeliveryFieldConfig } from "@checkout-v2/formFields/factories/addressHomeDeliveryFieldFactory";
import { ShippingAddressDialog } from "@checkout-v2/shared/components/ShippingAddressDialog";
import { useSetShippingAddressOnCart } from "@checkout-v2/shared/hooks/useSetShippingAddressOnCart/useSetShippingAddressOnCart";

import { AddressFormFields } from "../AddressFormFields";
import type { State as AtHomeAddressSelectionDialogState } from "../AtHomeAddressSelectionDialog";
import { AtHomeAddressSelectionDialog } from "../AtHomeAddressSelectionDialog";

import type { AddressHomeDeliveryFieldMode } from "./hooks";

import styles from "./AddressHomeDeliveryView.module.scss";

export type AddressHomeDeliveryViewProps = {
  fieldConfig: AddressHomeDeliveryFieldConfig;
  mode: AddressHomeDeliveryFieldMode;
  homeDeliveryAddress: AddressHomeDeliveryFieldValue;
  homeDeliveryAddressWithOverrides: AddressHomeDeliveryFieldValue;
  immutableProvinceCode?: string;
  shippingAddressErrorMessage?: string;
  isAddressDialogOpen: boolean;
  isSaveAddressPending: boolean;
  isOnDialogSubmitLoading: boolean;
  showShippingAddressSelector: () => void;
  hideShippingAddressSelector: () => void;
  showNewAddressForm: () => void;
  onAddressSelect: (address: AddressStored) => Promise<void>;
  saveNewAddress: (addressOverride?: AddressStored) => Promise<boolean>;
  renderSaveButton?: boolean;
};

export const AddressHomeDeliveryView: FC<AddressHomeDeliveryViewProps> = ({
  fieldConfig,
  mode,
  homeDeliveryAddress,
  homeDeliveryAddressWithOverrides,
  immutableProvinceCode,
  shippingAddressErrorMessage,
  isAddressDialogOpen,
  isSaveAddressPending,
  isOnDialogSubmitLoading,
  showShippingAddressSelector,
  hideShippingAddressSelector,
  showNewAddressForm,
  onAddressSelect,
  saveNewAddress,
  renderSaveButton = true,
}) => {
  const { __ } = useTranslations();
  const { ffCheckoutV2EnableIteration2 } = useFeatureFlags();
  const { setShippingAddressOnCartState } = useSetShippingAddressOnCart();

  const [combinedDialogState, setCombinedDialogState] = useState<AtHomeAddressSelectionDialogState>({ type: "closed" });

  const isCombinedAddressFormOpen = ffCheckoutV2EnableIteration2 && combinedDialogState.type === "address_form";

  const isSelectionFromSavedAddressesAvailable = fieldConfig.addresses.length > 1;

  const showAddressFormDialog = () => {
    setShippingAddressOnCartState.reset();

    setCombinedDialogState({
      type: "address_form",
      fieldConfig,
      immutableProvinceCode,
      onAddressSaveClick: saveNewAddress,
      onAddressFormCancelClick: showCombinedShippingAddressSelector,
    });
  };

  const showCombinedShippingAddressSelector = () => {
    setCombinedDialogState({
      type: "address_selection",
      addresses: fieldConfig.addresses,
      onCreateNew: showAddressFormDialog,
      onConfirm: (address: AddressStored) => {
        onAddressSelect(address);
        setCombinedDialogState({ type: "closed" });
      },
      preselectedAddress: homeDeliveryAddress,
      isOnSubmitLoading: setShippingAddressOnCartState.isLoading,
    });
  };

  const shippingAddressChangeCta = isSelectionFromSavedAddressesAvailable
    ? __("cart.checkout.shipping_address.edit")
    : __("cart.checkout.shipping_address.new");
  const onShippingAddressChange = () => {
    if (ffCheckoutV2EnableIteration2) {
      showCombinedShippingAddressSelector();
      return;
    }

    if (isSelectionFromSavedAddressesAvailable) {
      showShippingAddressSelector();
    } else {
      showNewAddressForm();
    }
  };

  return (
    <div
      className={styles.container}
      data-testid="address-home-delivery-field"
      tabIndex={-1}
      role="group"
      aria-label={__("cart.checkout.shipping_address.title")}
    >
      {shippingAddressErrorMessage && !isCombinedAddressFormOpen && (
        <AlertBanner tone="error" description={shippingAddressErrorMessage} />
      )}

      {mode === "new" && (
        <AddressFormFields fieldNameBase={fieldConfig.name}>
          {fieldConfig.addresses.length > 0 && (
            <Button
              type="button"
              variant="text"
              className={styles.selectExistingAddressButton}
              onClick={showShippingAddressSelector}
              data-testid="modify-address-button"
            >
              {__("cart.checkout.shipping_address.select_from_existing")}
            </Button>
          )}

          {renderSaveButton && (
            <Button
              type="button"
              onClick={() => saveNewAddress()}
              pending={isSaveAddressPending}
              data-testid="save-address-button"
            >
              {__("cart.checkout.shipping_address.new.save")}
            </Button>
          )}
        </AddressFormFields>
      )}

      {mode === "view" &&
        homeDeliveryAddress &&
        (ffCheckoutV2EnableIteration2 ? (
          <SelectedAddressViewCard
            title={__("cart.checkout.shipping_address.title")}
            changeBtnText={shippingAddressChangeCta}
            onChange={onShippingAddressChange}
          >
            <AddressInfoBlock address={homeDeliveryAddressWithOverrides} isEditable={false} hideTelephone />
          </SelectedAddressViewCard>
        ) : (
          <AddressInfoBlock
            address={homeDeliveryAddressWithOverrides}
            editLabel={shippingAddressChangeCta}
            onEdit={onShippingAddressChange}
            isEditable
            hideName
            hideTelephone
          />
        ))}

      {isAddressDialogOpen && (
        <ShippingAddressDialog
          open={isAddressDialogOpen}
          addresses={fieldConfig.addresses}
          onCreateNew={showNewAddressForm}
          onCancel={hideShippingAddressSelector}
          onConfirm={onAddressSelect}
          preselectedAddress={homeDeliveryAddress}
          isOnSubmitLoading={isOnDialogSubmitLoading}
        />
      )}

      {ffCheckoutV2EnableIteration2 && (
        <AtHomeAddressSelectionDialog
          state={combinedDialogState}
          shippingAddressErrorMessage={shippingAddressErrorMessage}
          onClose={() => {
            setCombinedDialogState({ type: "closed" });
          }}
        />
      )}
    </div>
  );
};
